/**
 * riskSettingsApi — the Risk-Management-owned settings, read and written
 * through routes that a RiskManager's own bearer satisfies (pcb-retail-api):
 *
 *   GET /v3/risk/settings              every Risk-owned key: value, default, control metadata
 *   PUT /v3/risk/settings              { settingKey, settingValue, reason } — one key per call
 *   GET /v3/risk/settings/effective    the value each live reader uses right now
 *   GET /v3/risk/settings/history      change log (who / when / old / new / reason)
 *   GET /v3/risk/gates                 FraudPoint gate strategy per customer type
 *   PUT /v3/risk/gates                 { gateKey, customerType, strategy, reason }
 *
 * Why this exists (2026-09-30): POST /v3/admin/system-settings is gated on the
 * ADMIN role, so a RiskManager could never save. Risk-owned settings have their
 * own route, gated on RiskManager (SuperAdmin always passes), with the allowed
 * keys and values fixed server-side (RiskSettingsService.Catalog).
 * 2026-10-07 (Bill): every change carries a reason and is written to the change
 * log in the same save as the value.
 *
 * Bearer-only (`Authorization: Bearer <msal>`); no function key ships in the
 * bundle.
 */
import { getMsalToken } from '@/auth/msal';

export type RiskSettingControl = 'toggle' | 'select' | 'number' | 'multi' | 'matrix';
export type RiskSettingGroup = 'identity' | 'sanctions' | 'compliance' | 'signing' | 'lnReportAccess';

export interface RiskSettingOption {
  readonly value: string;
  readonly label: string;
}

export interface RiskSettingRow {
  readonly settingKey: string;
  readonly settingValue: string | null;
  readonly defaultValue: string | null;
  readonly description: string | null;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
  /** True when no row exists yet and the server's default is in force. */
  readonly usingDefault: boolean;
  // Control metadata (2026-10-07). Optional so older servers still parse.
  readonly group?: RiskSettingGroup;
  readonly label?: string;
  readonly affects?: string;
  readonly control?: RiskSettingControl;
  readonly options?: ReadonlyArray<RiskSettingOption> | null;
  readonly min?: number | null;
  readonly max?: number | null;
  readonly unit?: string | null;
  readonly boundNote?: string | null;
  /** matrix: one row per choice; each row picks one of `options`. Value = JSON object row → option. */
  readonly rows?: ReadonlyArray<RiskSettingOption> | null;
  readonly citation?: string;
  readonly readBy?: string;
  readonly applies?: 'server' | 'officer-app';
  readonly takesEffect?: string;
}

export interface RiskSettingChange {
  readonly id: number;
  readonly changedAt: string;
  readonly changedBy: string;
  readonly kind: 'setting' | 'gate';
  readonly targetKey: string;
  readonly oldValue: string | null;
  readonly newValue: string | null;
  readonly reason: string;
}

export interface RiskGateRow {
  readonly gateKey: string;
  readonly gateName: string;
  readonly gateDescription: string | null;
  readonly customerType: string;
  readonly strategy: string;
  readonly isOverride: boolean;
  readonly citationKey: string;
  readonly citationLabel: string | null;
  readonly overrideReason: string | null;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
  readonly defaultStrategy: string;
  readonly strategies: ReadonlyArray<{ readonly value: string; readonly effect: string }>;
}

export interface RiskEffective {
  readonly readAt: string;
  readonly settings: Readonly<Record<string, string>>;
  readonly gates: Readonly<Record<string, string>>;
}

interface Envelope {
  success: boolean;
  error?: string;
  errorCode?: string;
}

/** Reason rules the server enforces (RiskSettingsService.ReasonMin/Max). */
export const REASON_MIN = 5;
export const REASON_MAX = 500;

export function getBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
    'https://pcb-retail-api.azurewebsites.net/api'
  );
}

export async function buildHeaders(): Promise<Headers> {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-pcb-client-id': 'risk-mgmt-panel',
  });
  try {
    const token = await getMsalToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // Silent — RoleGate already enforced an authenticated session.
  }
  return headers;
}

export async function readJsonOrThrow<T extends { success?: boolean; error?: string }>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: T | null = null;
  if (text) {
    try {
      body = JSON.parse(text) as T;
    } catch {
      body = null;
    }
  }
  if (!resp.ok || (body && body.success === false)) {
    const msg = body?.error ?? (resp.status === 403
      ? 'Your account is not set up to change Risk Management settings (RiskManager role required).'
      : `HTTP ${resp.status}`);
    throw new Error(msg);
  }
  if (!body) throw new Error('Empty response from the server.');
  return body;
}

export async function loadRiskSettings(): Promise<ReadonlyArray<RiskSettingRow>> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/settings`, { method: 'GET', headers: await buildHeaders() });
  const body = await readJsonOrThrow<Envelope & { settings?: RiskSettingRow[] }>(resp);
  return body.settings ?? [];
}

/** One key per call. `reason` is required (5-500 characters) and goes to the change log. */
export async function saveRiskSetting(settingKey: string, settingValue: string, reason: string): Promise<void> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/settings`, {
    method: 'PUT',
    headers: await buildHeaders(),
    body: JSON.stringify({ settingKey, settingValue, reason }),
  });
  await readJsonOrThrow<Envelope>(resp);
}

export async function loadRiskEffective(): Promise<RiskEffective> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/settings/effective`, { method: 'GET', headers: await buildHeaders() });
  const body = await readJsonOrThrow<Envelope & RiskEffective>(resp);
  return { readAt: body.readAt, settings: body.settings ?? {}, gates: body.gates ?? {} };
}

export async function loadRiskHistory(key?: string, take = 200): Promise<ReadonlyArray<RiskSettingChange>> {
  const qs = new URLSearchParams({ take: String(take) });
  if (key) qs.set('key', key);
  const resp = await fetch(`${getBaseUrl()}/v3/risk/settings/history?${qs.toString()}`, { method: 'GET', headers: await buildHeaders() });
  const body = await readJsonOrThrow<Envelope & { changes?: RiskSettingChange[] }>(resp);
  return body.changes ?? [];
}

export async function loadRiskGates(): Promise<{ gates: ReadonlyArray<RiskGateRow>; takesEffect: string }> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/gates`, { method: 'GET', headers: await buildHeaders() });
  const body = await readJsonOrThrow<Envelope & { gates?: RiskGateRow[]; takesEffect?: string }>(resp);
  return { gates: body.gates ?? [], takesEffect: body.takesEffect ?? '' };
}

export async function saveRiskGate(gateKey: string, customerType: string, strategy: string, reason: string): Promise<void> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/gates`, {
    method: 'PUT',
    headers: await buildHeaders(),
    body: JSON.stringify({ gateKey, customerType, strategy, reason }),
  });
  await readJsonOrThrow<Envelope>(resp);
}

export function findSetting(rows: ReadonlyArray<RiskSettingRow>, key: string): RiskSettingRow | undefined {
  return rows.find((r) => r.settingKey === key);
}
